import { FormStep } from 'src/components/stepper';
import { translateKey } from 'src/locales/translate';
import { MedicationsFormModalValuesProps } from './medicationFormSchema';
import { STOCK_FIELD_NAMES } from './medicationStockValues';

export type MedicationStepId =
  | 'medicine'
  | 'dosage'
  | 'course'
  | 'status'
  | 'reminder'
  | 'stock'
  | 'summary';

export interface MedicationFormStep extends FormStep {
  id: MedicationStepId;
  /**
   * What "Dalej" validates. Only this step's fields, so a vet is not told about
   * a field two steps ahead that they have not reached yet.
   */
  fields: (keyof MedicationsFormModalValuesProps)[];
}

const ALL_STEPS: MedicationFormStep[] = [
  {
    id: 'medicine',
    label: translateKey('medication.form.stepMedicine'),
    icon: 'mdi:pill',
    fields: ['name', 'medicineProductId', 'preparationType'],
  },
  {
    id: 'dosage',
    label: translateKey('medication.form.stepDosage'),
    icon: 'mdi:beaker-outline',
    fields: [
      'dosagePending',
      'strengthValue',
      'strengthUnit',
      'strengthUnitCustom',
      'form',
      'formCustom',
      'quantityValue',
      'quantityCustom',
      'frequencyType',
      'frequencyValue',
      'frequencyCustom',
      'durationType',
      'durationValue',
      'durationCustom',
      'dosageSchedule',
      'notes',
    ],
  },
  {
    id: 'course',
    label: translateKey('medication.form.stepCourse'),
    icon: 'mdi:clipboard-pulse-outline',
    fields: [
      'indication',
      'startedAt',
      'adverseReaction',
      'adverseReactionNote',
    ],
  },
  {
    id: 'status',
    label: translateKey('medication.form.stepStatus'),
    icon: 'mdi:play-pause',
    fields: ['status', 'stopReason', 'stopReasonNote', 'endedAt'],
  },
  {
    id: 'reminder',
    label: translateKey('medication.form.stepReminder'),
    icon: 'mdi:bell-outline',
    fields: [
      'reminderEnabled',
      'reminderDescription',
      'reminderScheduledAt',
      'reminderRecurrenceType',
      'reminderCustomEvery',
      'reminderCustomUnit',
      'reminderEmailEnabled',
      'reminderSmsEnabled',
    ],
  },
  {
    id: 'stock',
    label: translateKey('medication.form.stepStock'),
    icon: 'mdi:warehouse',
    // #692 rev B: last before the summary, so everything it works out from
    // (the dose, the course) is already known.
    fields: STOCK_FIELD_NAMES,
  },
  {
    id: 'summary',
    label: translateKey('medication.form.stepSummary'),
    icon: 'mdi:playlist-check',
    // Nothing of its own to validate — it reads back what the other steps hold.
    fields: [],
  },
];

/**
 * The steps this surface actually shows. The treatment editor has no lifecycle
 * and no reminder; a medication being added has no status to set either, since
 * that follows from its start date. "Magazyn" only once the form holds a
 * Warehouse Product (#692 rev B).
 */
export function buildMedicationSteps(options: {
  hideLifecycle?: boolean;
  hideReminder?: boolean;
  isEditing?: boolean;
  /** A Warehouse Product in the form: a step-1 pick, or a saved taking. */
  withStock?: boolean;
  /** A medication whose visit is locked: its status still moves, nothing else. */
  statusOnly?: boolean;
}): MedicationFormStep[] {
  if (options.statusOnly) {
    return ALL_STEPS.filter(({ id }) => id === 'status' || id === 'summary');
  }
  return ALL_STEPS.filter((step) => {
    if (step.id === 'course' && options.hideLifecycle) return false;
    if (step.id === 'status' && (options.hideLifecycle || !options.isEditing)) {
      return false;
    }
    if (step.id === 'reminder' && options.hideReminder) return false;
    if (step.id === 'stock' && !options.withStock) return false;
    return true;
  });
}
